<template>
  <div class="kanban-board">
    <!-- Header -->
    <b-row class="mb-2 align-items-center">
      <b-col
        cols="12"
        md="5"
      >
        <div class="d-flex align-items-start">
          <back-with-title
            v-if="board || milestone"
            :title="milestone ? milestone.name : board?.name"
            :description="milestone ? board?.name : board?.project?.name"
            :route-name="milestone ? 'project-board-milestones' : 'project-management'"
          >
            <template #append>
              <b-badge
                v-if="board && !milestone"
                v-b-tooltip.hover="'View Performance Report'"
                variant="light-primary"
                class="cursor-pointer"
                style="margin-left: 8px;"
                pill
                @click="$router.push({ name: 'project-board-member-report', params: { boardUid: base58Converter(board?.uuid) } })"
              >
                <feather-icon
                  icon="BarChart2Icon"
                />
              </b-badge>
            </template>
          </back-with-title>
        </div>
      </b-col>
      <b-col
        v-if="board"
        cols="12"
        md="7"
        class="text-right"
      >
        <div class="d-flex align-items-center justify-content-end board-header-actions">
          <!-- Search -->
          <b-input-group class="flex-grow-1 mr-2">
            <template #prepend>
              <b-input-group-text>
                <feather-icon
                  icon="SearchIcon"
                  size="16"
                />
              </b-input-group-text>
            </template>

            <b-form-input
              v-model="searchQuery"
              :placeholder="$t('Search tasks')"
              autocomplete="off"
              debounce="500"
            />

            <template #append>
              <b-input-group-text
                v-if="searchQuery"
                class="cursor-pointer"
                @click="searchQuery = ''"
              >
                <feather-icon
                  icon="XIcon"
                  size="14"
                />
              </b-input-group-text>
            </template>
          </b-input-group>

          <!-- Filter -->
          <filter-dropdown :include-filters="['assignees', 'labels', 'taskPriority']" />

          <!-- Members avatars preview -->
          <user-avatar-group
            :style="{
              width: board?.members?.data?.length < 4 ? board?.members?.data?.length * 68 + 'px' : '220px',
              left: board?.members?.data?.length < 4 ? board?.members?.data?.length * 3 + 'px' : '0'
            }"
            class="position-relative"
            :users="board?.members?.data || []"
            :loading="loading"
          />

          <!-- Quick Actions -->
          <div class="action-buttons d-flex align-items-center">
            <!-- Manage Permissions -->
            <manage-permissions
              v-if="canManagePermissions"
              :permissions="permissions"
              class="mr-1"
            />

            <!-- More actions dropdown -->
            <b-dropdown
              variant="link"
              toggle-class="p-0 board-actions-dropdown"
              :disabled="loading"
              no-caret
              right
            >
              <template #button-content>
                <div class="action-menu-btn">
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="20"
                  />
                </div>
              </template>
              <b-dropdown-item @click="openTaskPanel">
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-2"
                />
                {{ $t('Add New Task') }}
              </b-dropdown-item>

              <div v-if="canManageBoards">
                <b-dropdown-item @click="showMembersModal = true">
                  <feather-icon
                    icon="UsersIcon"
                    size="16"
                    class="mr-2"
                  />
                  {{ $t('Manage Members') }}
                </b-dropdown-item>
                <b-dropdown-item @click="showStatusManager = true">
                  <feather-icon
                    icon="SlidersIcon"
                    size="16"
                    class="mr-2"
                  />
                  {{ $t('Manage Statuses') }}
                </b-dropdown-item>
                <b-dropdown-item v-b-modal.manage-labels-modal>
                  <feather-icon
                    icon="TagIcon"
                    size="16"
                    class="mr-2"
                  />
                  {{ $t('Manage Labels') }}
                </b-dropdown-item>
                <b-dropdown-divider />
              </div>

              <div v-if="!$route.params.milestoneUid">
                <b-dropdown-item
                  @click="$router.push({ name: 'project-board-milestones', params: { boardUid: base58Converter(board.uuid) } })"
                >
                  <feather-icon
                    icon="CrosshairIcon"
                    size="16"
                    class="mr-2"
                  />
                  {{ $t('Milestones') }}
                </b-dropdown-item>
                <b-dropdown-divider />
              </div>

              <b-dropdown-item @click="$router.push({ name: 'project-management' })">
                <feather-icon
                  icon="GridIcon"
                  size="16"
                  class="mr-2"
                />
                {{ $t('All Boards') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Kanban Columns -->
    <b-overlay
      :show="board && loading"
      rounded="sm"
    >
      <b-skeleton-wrapper
        :loading="!board && loading"
      >
        <template #loading>
          <div class="d-flex justify-content-between">
            <div
              v-for="i in 4"
              :key="i"
              class="border ml-1 w-25"
            >
              <b-card
                v-for="j in 3"
                :key="j"
                class="border m-1"
              >
                <b-skeleton
                  v-for="k in 3"
                  :key="k"
                  :width="`${Math.floor(k * (35 - j * i))}%`"
                />
              </b-card>
            </div>
          </div>
        </template>

        <template #default>
          <div
            v-if="board"
            class="kanban-container"
          >
            <div
              v-for="status in statuses"
              :key="status.id"
              class="kanban-column"
            >
              <div
                class="column-header"
                :style="{ borderTopColor: status.color || '#94a3b8' }"
              >
                <div class="d-flex align-items-center">
                  <span
                    class="status-dot mr-1"
                    :style="{ backgroundColor: status.color || '#94a3b8' }"
                  />
                  <span class="font-weight-bold">{{ status.name }}</span>
                  <b-badge
                    variant="light-secondary"
                    class="ml-1"
                  >
                    {{ status.tasks.total }}
                  </b-badge>
                  <feather-icon
                    v-if="status.isFinal"
                    v-b-tooltip="$t('Final Status')"
                    icon="CheckCircleIcon"
                    size="14"
                    class="ml-50 text-success"
                  />
                </div>
              </div>

              <div
                class="column-tasks-overlay"
                :style="{ overflowY: 'auto' }"
                @scroll="e => fetchMoreTasks(e, status)"
              >
                <draggable
                  :list="status.tasks.data"
                  :group="{ name: 'tasks' }"
                  class="h-100 column-tasks"
                  ghost-class="task-ghost"
                  @change="(evt) => onTaskDrop(evt, status)"
                >
                  <task-card
                    v-for="task in status.tasks.data"
                    :key="task.uuid"
                    :task="task"
                    @click="editTask(task)"
                    @edit="task => { isEditingTask = true; editTask(task) }"
                    @delete="confirmDeleteTask(task)"
                    @refresh="fetchBoard({ uuid: board.uuid })"
                  />
                </draggable>
              </div>

              <div
                v-if="GET_STATUS_PAGES[status.key]?.fetchingMoreTasks"
                class="w-full text-center mt-2"
              >
                <b-spinner />
              </div>

              <b-button
                variant="flat-secondary"
                block
                size="sm"
                class="add-task-btn"
                @click="addTaskToStatus(status)"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="14"
                />
                {{ $t('Add') }}
              </b-button>
            </div>
          </div>

          <!-- No Access Alert -->
          <b-alert
            v-else-if="!loading"
            variant="danger"
            show
            class="p-2 text-center"
          >
            <div>
              <feather-icon
                icon="FrownIcon"
                size="72"
                class="mb-1"
              />
            </div>
            <h1 class="text-danger">
              {{ $t('Oops!') }}
            </h1>
            <p>
              {{ $t('You do not have access to this board or it does not exist.') }}
            </p>
          </b-alert>
        </template>
      </b-skeleton-wrapper>

    </b-overlay>

    <!-- Task Side Panel -->
    <task-side-panel
      v-if="board"
      :visible="showTaskPanel"
      :task="editingTask"
      :editing-mode="isEditingTask"
      :board="board"
      :status-id="selectedStatusId"
      @submit="submitTask"
      @close="closeTaskPanel"
    />

    <!-- Status Manager Modal -->
    <b-modal
      v-model="showStatusManager"
      :title="$t('Manage Statuses')"
      size="lg"
      hide-footer
      centered
    >
      <status-manager
        :board="board"
        @close="showStatusManager = false"
        @updated="loadBoard"
      />
    </b-modal>

    <!-- Members Manager Modal -->
    <b-modal
      v-model="showMembersModal"
      :title="$t('Manage Board Members')"
      size="lg"
      hide-footer
      centered
    >
      <board-members-manager
        :board="board"
        @close="showMembersModal = false"
        @updated="loadBoard"
      />
    </b-modal>

    <!-- Label Manager Modal -->
    <label-manager @refresh="loadBoard" />

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteTaskModal"
      :title="$t('Delete Task')"
      hide-footer
      centered
    >
      <p>{{ $t('Are you sure you want to delete this task?') }}</p>
      <p class="text-danger">
        <strong>{{ taskToDelete?.title }}</strong>
      </p>
      <div class="d-flex justify-content-end">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="showDeleteTaskModal = false"
        >
          {{ $t('Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="submitting"
          @click="deleteTask"
        >
          <b-spinner
            v-if="submitting"
            small
            class="mr-50"
          />
          {{ $t('Delete') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BRow,
  BCol,
  BButton,
  BBadge,
  BOverlay,
  BModal,
  BSpinner,
  BCard,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BSkeletonWrapper,
  BSkeleton,
  BFormInput,
  BInputGroup,
  BInputGroupText,
  VBTooltip,
} from 'bootstrap-vue'
import draggable from 'vuedraggable'
import { mapGetters, mapActions } from 'vuex'
import UserAvatarGroup from '@/views/common/components/UserAvatarGroup.vue'
import ManagePermissions from '@/views/common/components/ManagePermissions.vue'
import EventBus from '@/event-bus'
import { checkPermission } from '@/utils/permissions'
import FilterDropdown from '@/views/common/components/Filters/FilterDropdown.vue'
import useFilters from '@/views/common/components/Filters/useFilters'
import BackWithTitle from '@/views/common/components/BackWithTitle.vue'
import TaskCard from '../tasks/TaskCard.vue'
import TaskSidePanel from '../tasks/TaskSidePanel.vue'
import StatusManager from './StatusManager.vue'
import BoardMembersManager from './BoardMembersManager.vue'
import LabelManager from './LabelManager.vue'

const {
  getActiveFilters,
  setFilters,
} = useFilters()

export default {
  name: 'KanbanBoard',
  components: {
    BAlert,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BInputGroup,
    BInputGroupText,
    BBadge,
    BCard,
    BOverlay,
    BSkeletonWrapper,
    BSkeleton,
    BModal,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    draggable,
    TaskCard,
    TaskSidePanel,
    StatusManager,
    BoardMembersManager,
    UserAvatarGroup,
    ManagePermissions,
    LabelManager,
    FilterDropdown,
    BackWithTitle,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      searchQuery: '',
      showTaskPanel: false,
      showStatusManager: false,
      showMembersModal: false,
      showDeleteTaskModal: false,
      editingTask: null,
      taskToDelete: null,
      selectedStatusId: null,
      isEditingTask: false,
      submitting: false,
      permissions: ['boards.manage', 'boards.edit'],
      needPermissionCheck: false,
    }
  },
  computed: {
    ...mapGetters('boards', ['GET_CURRENT_BOARD', 'GET_CURRENT_MILESTONE_BOARD', 'GET_FETCHING_BOARD', 'GET_LOADING', 'GET_STATUS_PAGES']),
    ...mapGetters('permissions', ['canManagePermissions', 'canManageBoards', 'canEditBoards']),
    board() {
      return this.GET_CURRENT_MILESTONE_BOARD ?? this.GET_CURRENT_BOARD
    },
    loading() {
      return this.GET_FETCHING_BOARD || this.GET_LOADING
    },
    statuses() {
      return (this.board?.statuses || []).sort((a, b) => a.position - b.position)
    },
    members() {
      return this.board?.members?.data || []
    },
    labels() {
      return this.board?.labels?.data || []
    },
    activeFilters() {
      return getActiveFilters.value
    },
    milestone() {
      const { milestoneUid } = this.$route.params

      if (!milestoneUid) return null
      return this.GET_CURRENT_BOARD?.milestones?.data.find(m => m.uuid === this.base58Converter(milestoneUid, false)) || null
    },
    taskSearch() {
      return {
        query: this.searchQuery,
        includeComments: true,
      }
    },
  },
  watch: {
    '$route.params.boardUid': {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadBoard()
        }
      },
    },
    '$route.params.taskUid': {
      immediate: true,
      handler(val) {
        if (val) {
          this.editTask({ uuid: this.base58Converter(val, false) })
        }
      },
    },
    members(value) {
      if (value) {
        setFilters('assignees', [
          { uuid: 'unassigned', name: this.$t('Unassigned'), icon: 'UserIcon' },
          ...value,
        ])
      }
    },
    labels(value) {
      if (value) {
        setFilters('labels', value)
      }
    },
    activeFilters: {
      handler() {
        if (this.board) this.getBoard()
      },
      deep: true,
    },
    searchQuery() {
      if (this.board) this.getBoard()
    },
  },
  mounted() {
    EventBus.$on('permission-updated', permission => {
      if (this.permissions.indexOf(permission) !== -1) {
        this.needPermissionCheck = true
        setTimeout(() => this.checkUserPermission(), 2000)
      }
    })
    this.checkUserPermission()
  },
  beforeDestroy() {
    EventBus.$off('permission-updated')
  },
  methods: {
    ...mapActions('boards', ['fetchBoard', 'fetchTask', 'moveTask', 'createTask', 'updateTask', 'deleteTask', 'fetchTasksByStatusKey']),
    ...mapActions('permissions', ['setCanManageBoards', 'setCanEditBoards']),
    checkUserPermission() {
      if (this.needPermissionCheck || !this.canManageBoards || !this.canEditBoards) {
        checkPermission(this.permissions, permission => {
          const canManageBoards = permission['boards.manage'] === true
          const canEditBoards = permission['boards.edit'] === true
          this.setCanManageBoards(canManageBoards)
          this.setCanEditBoards(canEditBoards)
          this.needPermissionCheck = false
        })
      }
    },
    async loadBoard() {
      try {
        await this.fetchBoard({
          uuid: this.base58Converter(this.$route.params.boardUid, false),
          milestoneUid: this.$route.params.milestoneUid ? this.base58Converter(this.$route.params.milestoneUid, false) : null,
        })
      } catch (error) {
        this.showError(error.message)
      }
    },
    getTasksForStatus(statusId) {
      return this.tasks.filter(t => t.status?.id === statusId)
    },
    addTaskToStatus(status) {
      this.selectedStatusId = status.id
      this.editingTask = null
      this.showTaskPanel = true
    },
    openTaskPanel(statusId = null) {
      this.selectedStatusId = statusId
      this.editingTask = null
      this.showTaskPanel = true
    },
    async editTask(task) {
      this.editingTask = await this.fetchTask(task.uuid)
      this.selectedStatusId = task.status?.id
      this.showTaskPanel = true
    },
    closeTaskPanel() {
      this.showTaskPanel = false
      this.isEditingTask = false
      this.editingTask = null
      this.selectedStatusId = null
    },
    async submitTask(taskData) {
      this.submitting = true
      try {
        const payload = {
          ...taskData,
          projectUid: taskData.projectUid || this.board?.project?.uuid,
        }

        if (this.editingTask) {
          const result = await this.updateTask({
            uuid: this.editingTask.uuid,
            input: payload,
          })
          this.showSuccess(result.message)
        } else {
          const { projectUid, ...data } = payload
          const result = await this.createTask({
            boardUid: this.board.uuid,
            projectUid,
            input: data,
          })
          this.showSuccess(result.message)
        }
        this.closeTaskPanel()
        this.loadBoard()
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.submitting = false
      }
    },
    async onTaskDrop(evt, targetStatus) {
      if (evt.added) {
        const task = evt.added.element
        try {
          await this.moveTask({
            taskUid: task.uuid,
            currentStatusId: task.status.id,
            targetStatusId: targetStatus.id,
          })
        } catch (error) {
          this.showError(error.message)
          this.loadBoard()
        }
      }
    },
    confirmDeleteTask(task) {
      this.taskToDelete = task
      this.showDeleteTaskModal = true
    },
    async deleteTask() {
      this.submitting = true
      try {
        const result = await this.$store.dispatch('boards/deleteTask', this.taskToDelete.uuid)
        this.showSuccess(result.message)

        this.showDeleteTaskModal = false
        this.taskToDelete = null
        this.loadBoard()
      } catch (error) {
        this.showError(error.message)
      } finally {
        this.submitting = false
      }
    },
    async fetchMoreTasks(e, status) {
      const { scrollTop, offsetHeight, scrollHeight } = e.target
      if (scrollHeight - (scrollTop + offsetHeight) < 5 && status.tasks.hasMorePages) {
        if (!this.GET_STATUS_PAGES[status.key]?.fetchingMoreTasks) {
          const assignees = this.activeFilters?.assignees || []
          const labelUids = this.activeFilters?.labels?.map(item => item.uuid) || []
          const priority = this.activeFilters?.taskPriority

          await this.fetchTasksByStatusKey({
            statusKey: status.key, assignees, labelUids, priority, search: this.taskSearch,
          })
        }
      }
    },
    getBoard() {
      const assignees = this.activeFilters?.assignees || []
      const labelUids = this.activeFilters?.labels?.map(item => item.uuid) || []
      const statusKeys = this.activeFilters?.statuses?.map(item => item.key) || []
      const priority = this.activeFilters?.taskPriority

      this.fetchBoard({
        uuid: this.board?.uuid,
        assignees,
        labelUids,
        statusKeys,
        priority,
        search: this.taskSearch,
        milestoneUid: this.$route.params.milestoneUid ? this.base58Converter(this.$route.params.milestoneUid, false) : null,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/base/pages/kanban-board.scss";
</style>
